import { useEffect, useRef, useState } from "react";
import type { HolidayBriefingConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { tx, txr } from "../lib/tx";

type Stage = "inbox" | "email" | "watch" | "takeaway";

export function HolidayBriefing({
  config,
  onDone,
  onStageChange,
}: {
  config: HolidayBriefingConfig;
  onDone: () => void;
  onStageChange?: (stage: string) => void;
}) {
  const [stage, setStage] = useState<Stage>("inbox");
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [fact, setFact] = useState<"arup" | "google" | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => onStageChange?.(stage), [onStageChange, stage]);

  useEffect(() => {
    if (stage !== "watch") return;
    audio.duckScene(0);
    const video = videoRef.current;
    if (video) {
      video.preload = "auto";
      video.load();
    }
    return () => {
      audio.duckScene(1);
      video?.pause();
    };
  }, [stage]);

  const play = () => {
    const video = videoRef.current;
    if (!video || buffering) return;
    const start = () => {
      void video.play().then(() => {
        setPlaying(true);
        setBuffering(false);
      }).catch(() => {
        setPlaying(false);
        setBuffering(false);
      });
    };
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      start();
      return;
    }
    setBuffering(true);
    const onReady = () => {
      video.removeEventListener("canplaythrough", onReady);
      start();
    };
    video.addEventListener("canplaythrough", onReady);
  };

  const syncFact = () => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const progress = video.currentTime / video.duration;
    setFact(
      progress >= 0.08 && progress < 0.38
        ? "arup"
        : progress >= 0.52 && progress < 0.84
          ? "google"
          : null,
    );
  };

  return (
    <section className={`holiday-briefing hb-stage-${stage}`}>
      {stage === "inbox" && (
        <div className="hb-mail-client">
          <header><span className="hb-mail-app">{tx("M")}</span><b>{tx("Aureon Mail")}</b><div>{tx("Search mail")}</div><span>{tx("Inbox · 1 unread")}</span></header>
          <div className="hb-mail-layout">
            <nav><button>{tx("＋ New message")}</button><strong>{tx("Inbox")} <i>1</i></strong><span>{tx("Flagged")}</span><span>{tx("Sent")}</span><span>{tx("Drafts")}</span><span>{tx("Archive")}</span></nav>
            <aside>
              <h3>{tx("Inbox")}</h3>
              <button className="hb-message-row unread cf-pulse-target" onClick={() => setStage("email")}>
                <span className="hb-shield">!</span>
                <span><strong>{config.sender}</strong><b>{config.subject}</b><small>{config.intro}</small></span>
                <time>15:47</time><em>{tx("Click to open")}</em>
              </button>
              <button><span className="hb-sender-dot">{tx("HR")}</span><span><strong>{tx("Aureon HR")}</strong><b>{tx("Half-year town hall recording")}</b><small>{tx("The recording and slides are now available.")}</small></span><time>14:03</time></button>
              <button><span className="hb-sender-dot">{tx("TB")}</span><span><strong>{tx("Thomas Berger")}</strong><b>{tx("Enjoy the break")}</b><small>{tx("See you when you're back next week.")}</small></span><time>{tx("Thu")}</time></button>
            </aside>
            <main className="hb-mail-preview">
              <small>{tx("MESSAGE PREVIEW")}</small>
              <div className="hb-preview-sender"><span className="hb-shield">!</span><div><strong>{config.sender}</strong><small>security@aureon.example</small></div></div>
              <h2>{config.subject}</h2>
              <p>{config.intro}</p>
              <div className="hb-preview-mask"><span /><span /><span /></div>
              <button className="cf-pulse-target hb-open-full" onClick={() => setStage("email")}>
                {txr("Open full email <0>Click here</0>", {}, [<em className="ht-click-chip" />])}
              </button>
            </main>
          </div>
        </div>
      )}

      {stage === "email" && (
        <div className="hb-mail-client hb-mail-opened">
          <header><span className="hb-mail-app">{tx("M")}</span><b>{tx("Aureon Mail")}</b><div>{tx("Search mail")}</div><span>{tx("Inbox · 0 unread")}</span></header>
          <div className="hb-mail-layout">
            <nav><button>{tx("＋ New message")}</button><strong>{tx("Inbox")}</strong><span>{tx("Flagged")}</span><span>{tx("Sent")}</span><span>{tx("Drafts")}</span><span>{tx("Archive")}</span></nav>
            <aside>
              <h3>{tx("Inbox")}</h3>
              <button className="hb-message-row selected" onClick={() => setStage("email")}>
                <span className="hb-shield">!</span>
                <span><strong>{config.sender}</strong><b>{config.subject}</b><small>{config.intro}</small></span>
                <time>15:47</time>
              </button>
              <button onClick={() => setStage("inbox")}><span className="hb-sender-dot">{tx("HR")}</span><span><strong>{tx("Aureon HR")}</strong><b>{tx("Half-year town hall recording")}</b><small>{tx("The recording and slides are now available.")}</small></span><time>14:03</time></button>
            </aside>
            <main className="hb-email">
              <div className="hb-email-toolbar"><button onClick={() => setStage("inbox")}>{tx("← Back")}</button><span>{tx("Archive · Report · Delete")}</span></div>
              <h1>{config.subject}</h1>
              <div className="hb-email-from"><span className="hb-shield">!</span><div><b>{config.sender}</b><small>{tx("security@aureon.example · To Elena Fischer")}</small></div><time>{tx("Today, 15:47")}</time></div>
              <div className="hb-email-brand"><span>{tx("AT")}</span><div><b>{tx("Aureon Threat Briefing")}</b><small>{tx("Security awareness update")}</small></div></div>
              <p>{tx("Hello Elena,")}</p>
              <p>{config.intro}</p>
              <div className="hb-email-warning">
                {tx("Criminal groups now combine researched identities, cloned voices and generated video. Two real cases show how credibility is built before the request arrives.")}
              </div>
              <button className="hb-watch-cta cf-pulse-target" onClick={() => setStage("watch")}>
                <span>▶</span><b>{tx("Watch the 3-minute briefing")}</b><small>{tx("Open the security video")}</small><em className="ht-click-chip">{tx("Click here")}</em>
              </button>
              <p className="hb-email-signoff">{txr("Aureon Security Awareness{br}<0>This message was sent through the internal company mail system.</0>", { br: <br /> }, [<small />])}</p>
            </main>
          </div>
        </div>
      )}

      {stage === "watch" && (
        <div className="hb-youtube">
          <header><span className="hb-yt-logo">▶</span><b>{tx("Video")}</b><div>{tx("Search")}</div><span>⋮</span></header>
          <main>
            <div className="hb-player">
              <video
                ref={videoRef}
                src={config.videoUrl}
                poster={config.posterUrl}
                playsInline
                preload="auto"
                controls={playing}
                onWaiting={() => setBuffering(true)}
                onPlaying={() => {
                  setPlaying(true);
                  setBuffering(false);
                }}
                onTimeUpdate={syncFact}
                onEnded={() => {
                  setFact(null);
                  setBuffering(false);
                  setStage("takeaway");
                }}
              />
              {!playing && !buffering && (
                <button className="hb-big-play cf-pulse-target" onClick={play}>
                  <span>▶</span><b>{tx("Play briefing")}</b><small className="cf-click-here">{tx("Click here")}</small>
                </button>
              )}
              {buffering && (
                <div className="hb-buffering" aria-live="polite">{tx("Loading briefing…")}</div>
              )}
              {fact === "arup" && (
                <div className="hb-fact hb-fact-arup is-visible">
                  <small>{tx("ARUP · HONG KONG · 2024")}</small><strong>{tx("15 transfers")}</strong><b>{tx("HK$200 million")}</b><span>{tx("Fake voices and images impersonated colleagues in a video meeting.")}</span>
                </div>
              )}
              {fact === "google" && (
                <div className="hb-fact hb-fact-google is-visible">
                  <small>{tx("GOOGLE TAG · 2021")}</small><strong>{tx("Trust before attack")}</strong><span>{tx("Fake researcher identities, a blog and genuine community interaction built credibility before malicious collaboration files were shared.")}</span>
                </div>
              )}
            </div>
            <h2>{config.videoTitle}</h2>
            <div className="hb-channel"><span>{config.presenterName.slice(0, 1)}</span><div><b>{config.presenterName}</b><small>{config.presenterRole}</small></div></div>
            <p>{tx("These are two real attacks that happened. They are shown here so you can recognise the same pattern in your own work.")}</p>
            <div className="hb-sources">
              <a href={config.arupSourceUrl} target="_blank" rel="noreferrer">{tx("Arup statement and reporting")}</a>
              <a href={config.googleSourceUrl} target="_blank" rel="noreferrer">{tx("Google Threat Analysis Group")}</a>
            </div>
            {playing && <button className="hb-skip" onClick={() => setStage("takeaway")}>{tx("Skip to the next lesson")}</button>}
          </main>
        </div>
      )}

      {stage === "takeaway" && (
        <div className="hb-takeaway">
          <small>{tx("WHAT THESE CASES HAVE IN COMMON")}</small>
          <h1>{tx("The attack begins before the call.")}</h1>
          <p>{tx("In 2024, criminals used fabricated voices and images in a video conference to convince an Arup finance employee to make 15 transfers worth HK$200 million. In another documented campaign, fake researcher identities and months of credible online activity built trust before malicious files were introduced.")}</p>
          <div className="hb-chain">
            <span>{tx("Research identities")}</span><i>→</i><span>{tx("Build credibility")}</span><i>→</i><span>{tx("Copy face and voice")}</span><i>→</i><span>{tx("Create urgency")}</span><i>→</i><span>{tx("Request action")}</span>
          </div>
          <p>{tx("Now step into the attacker's workspace and see how public information, copied identities and AI can be assembled into a convincing holiday call against Elena.")}</p>
          <button className="ht-primary cf-pulse-target ht-action-target" onClick={onDone}>{txr("Enter the attacker workspace<0>Click here</0>", {}, [<em className="ht-click-chip" />])}</button>
        </div>
      )}
    </section>
  );
}
